import OpenInNew from "@mui/icons-material/OpenInNew";
import { IconButton, Stack, Tooltip, Button } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { StatusChip } from "components/StatusChip";
import { WASTE_PIPELINE_APPROVAL_LABELS } from "encamp-shared/src/pipelines";
import {
  StaffPipelinesTableQuery,
  StaffPipelinesTableQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import { STAFF_PIPELINES_TABLE_QUERY } from "./api";
import { DispatchWastePipelineRunDialog } from "./DispatchWastePipelineRunDialog";
import {
  getIngestionStatusColor,
  getRunStatusChip,
  INGEST_APPROVAL_CHIP_COLORS,
} from "./pipelineUtils";

type PipelineRow = StaffPipelinesTableQuery["pipelines"]["items"][number];

export const PipelineRuns = () => {
  const navigate = useNavigate();
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);

  const columns: OmnisearchGridColDef<PipelineRow>[] = useMemo(
    () => [
      {
        headerName: "Organization",
        field: "tenant",
        flex: 1.2,
        sortable: false,
        filterKeyType: "multipleTenant",
        valueGetter(_, row) {
          return row.tenant?.name ?? "";
        },
      },
      {
        headerName: "Pipeline Name",
        field: "name",
        flex: 1.2,
      },
      {
        headerName: "Run Status",
        field: "runJob",
        flex: 1,
        sortable: false,
        renderCell({ row }) {
          const { label, color } = getRunStatusChip(row);
          return <StatusChip label={label} color={color} />;
        },
      },
      {
        headerName: "Last Run",
        field: "lastRunAt",
        filterKeyType: "time",
        flex: 1,
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "Never";
        },
      },
      {
        headerName: "Ingested",
        field: "ingestedAt",
        flex: 1,
        sortable: false,
        renderCell({ row }) {
          if (!row.ingestedAt) return "Not ingested";
          const status = row.ingestionJob?.status;
          const color = getIngestionStatusColor(status);
          const label =
            status === "SUCCEEDED" ? prettyPrintDateTime(row.ingestedAt) : (status ?? "Unknown");
          return <StatusChip label={label} color={color} />;
        },
      },
      {
        headerName: "Ingest Approval",
        field: "ingestApproval",
        flex: 1,
        sortable: false,
        renderCell({ row }) {
          if (!row.ingestApproval) return "—";
          return (
            <StatusChip
              label={WASTE_PIPELINE_APPROVAL_LABELS[row.ingestApproval]}
              color={INGEST_APPROVAL_CHIP_COLORS[row.ingestApproval]}
            />
          );
        },
      },
      {
        headerName: "Created At",
        field: "createdAt",
        flex: 1.2,
        filterKeyType: "time",
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        headerName: "Created By",
        field: "createdBy",
        flex: 1.2,
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        filterKeyType: "time",
        flex: 1.2,
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        type: "actions",
        field: "actions",
        getActions: ({ row }) => [
          <Tooltip key="open" title="Open in new tab">
            <IconButton
              component={Link}
              to={`./${row.id}/results`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNew />
            </IconButton>
          </Tooltip>,
        ],
      },
    ],
    []
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<StaffPipelinesTableQuery, StaffPipelinesTableQueryVariables, PipelineRow>
        persistenceKey="staffPipelines"
        isURLDriven
        rowHeight="auto"
        initialSortModel={[{ field: "updatedAt", sort: "desc" }]}
        onRowClick={(params) => {
          navigate(`./${params.row.id}/results`);
        }}
        columns={columns}
        dataQuery={STAFF_PIPELINES_TABLE_QUERY}
        getItems={(data) => data.pipelines.items}
        getCount={(data) => data.pipelines.count}
        commandButtons={[
          <Button
            key="dispatch-waste-pipeline"
            variant="contained"
            color="secondary"
            onClick={() => setShowDispatchDialog(true)}
          >
            New Pipeline Run
          </Button>,
        ]}
      />
      {showDispatchDialog && (
        <DispatchWastePipelineRunDialog onClose={() => setShowDispatchDialog(false)} />
      )}
    </Stack>
  );
};
